<script setup lang="ts">
import { useToast } from '@/use/toast';
import { STRIPE_PUBLISHED_KEY } from '@/lib/env';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { isHandledStripeError, usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';
import { Alert, Button, LayoutGap } from '@dabble/quillui';
import type { StripeElements, StripePaymentElementOptions } from '@stripe/stripe-js';
import type Stripe from 'stripe';
import { onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

interface RouteMeta {
  customer?: Stripe.Customer;
}

/** Non-empty string for Stripe `defaultValues.billingDetails`; omit empty/null. */
function billingString(value: string | null | undefined): string | undefined {
  if (value == null || typeof value !== 'string') return undefined;
  const t = value.trim();
  return t.length > 0 ? t : undefined;
}

function billingNameFromSources(meta: Stripe.Customer | undefined, userName: string | undefined): string | undefined {
  return billingString(meta?.name) ?? billingString(userName);
}

function billingEmailFromSources(meta: Stripe.Customer | undefined, userEmail: string | undefined): string | undefined {
  return billingString(meta?.email) ?? billingString(userEmail);
}

const emit = defineEmits<{
  done: [];
  cancel: [];
}>();

const { t } = useI18n();
const { showToast } = useToast();
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const paymentStore = usePaymentStore();

const customerFromMeta = (route.meta as RouteMeta).customer;

const paymentContainer = ref<HTMLDivElement | null>(null);

let elements: StripeElements | null = null;
let alive = true;

/** Load Stripe, mint a SetupIntent, and mount the Payment Element. Re-runnable. */
async function buildForm(): Promise<void> {
  paymentStore.clearError();
  try {
    const stripe = await paymentStore.initializeStripe(STRIPE_PUBLISHED_KEY);
    if (!alive) return;

    const clientSecret = await paymentStore.createSetupIntent();
    if (!alive) return;

    const customerSessionClientSecret = paymentStore.customerSessionClientSecret || undefined;
    elements = stripe.elements({
      clientSecret,
      ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
      loader: 'auto',
      appearance: {
        theme: 'stripe',
      },
    });
    paymentStore.setElements(elements);

    const billingName = billingNameFromSources(customerFromMeta, userStore.user.name);
    const billingEmail = billingEmailFromSources(customerFromMeta, userStore.user.email);

    const paymentOptions: StripePaymentElementOptions = {
      defaultValues: {
        billingDetails: {
          ...(billingName ? { name: billingName } : {}),
          ...(billingEmail ? { email: billingEmail } : {}),
        },
      },
      wallets: { applePay: 'auto', googlePay: 'auto' },
    };
    const paymentElement = elements.create('payment', paymentOptions);
    paymentStore.setPaymentElement(paymentElement);

    if (paymentContainer.value && alive) {
      paymentElement.mount(paymentContainer.value);
    }
  } catch (error) {
    logError('Failed to initialize payment form:', error);
    showToast('danger', t('payment_error_unexpected'));
  }
}

onMounted(() => {
  void buildForm();
});

onUnmounted(() => {
  alive = false;
  paymentStore.teardownPaymentForm();
});

function currentReturnUrl(): string {
  return new URL(router.currentRoute.value.fullPath, window.location.origin).href;
}

async function savePaymentMethod() {
  if (!elements) return;

  try {
    const { error } = await elements.submit();
    if (error) {
      if (error.type === 'card_error' || error.type === 'validation_error') {
        showToast('danger', error.message ?? t('payment_error_unexpected'));
      } else {
        showToast('danger', t('payment_error_unexpected'));
      }
      return;
    }

    await paymentStore.confirmSetup(currentReturnUrl());
    emit('done');
  } catch (error) {
    // The store has already reported its own Stripe failures (DAB-971); log the rest,
    // e.g. the default-payment-method request after Stripe accepted the card.
    if (!isHandledStripeError(error)) logError('Failed to save payment method:', error);
    const message = error instanceof Error ? error.message : t('payment_error_unexpected');
    showToast('danger', message);
    // Once Stripe has accepted the SetupIntent it is spent, and it stays spent whatever
    // failed afterwards (the default-payment-method request, a network blip). Every
    // further Save against it answers `setup_intent_unexpected_state`, so rebuild on a
    // fresh one rather than leaving the form dead until a reload (DAB-1407). The toast
    // above already carries the reason; buildForm clears the Alert, not that.
    if (paymentStore.setupIntentConsumed && alive) {
      paymentStore.teardownPaymentForm();
      elements = null;
      await buildForm();
    }
  }
}

function onCancel() {
  emit('cancel');
}
</script>

<template>
  <LayoutGap direction="column" size="medium" class="billing-payment-method-form">
    <div class="billing-payment-method-form__error" role="alert" aria-live="assertive" aria-atomic="true">
      <!-- Body goes in the default slot: Alert has no `message` prop, so passing one
           silently rendered a bare title with no reason (DAB-957). -->
      <Alert v-if="paymentStore.error" variant="danger" :title="t('billing_payment_error')">
        {{ paymentStore.error }}
      </Alert>
    </div>

    <div ref="paymentContainer" class="billing-payment-method-form__stripe"></div>

    <LayoutGap
      direction="row"
      align="space-between"
      vertical-align="center"
      wrap
      size="small"
      class="billing-payment-method-form__actions"
    >
      <Button @click="onCancel">{{ t('billing_go_back') }}</Button>
      <Button variant="primary" :disabled="paymentStore.saving" @click="savePaymentMethod">
        {{ t('billing_payment_add') }}
      </Button>
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.billing-payment-method-form {
  max-width: none;
  margin: 0;
  padding: 0;

  &__stripe {
    min-height: 144px;
    margin-bottom: 1.5rem;
    min-width: 0;
    width: 100%;
  }

  &__actions {
    width: 100%;
  }

  &__error {
    color: #fa755a;
    margin-bottom: 16px;
    padding: 12px;
    background: #fff5f5;
    border-radius: 4px;
  }
}
</style>
